import { Button } from "@/components/ui/button";
import { Plus, Pencil, Trash2, Archive, CalendarClock, FileText } from "lucide-react";
import { TRIP_TYPE_EMOJI, tripTitle, tripDatesLabel } from "../../lib/trips";

const StatusBadge = ({ status }) =>
  status === "history" ? (
    <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-semibold bg-slate-100 text-slate-600 px-2 py-0.5 rounded-full">
      <Archive className="w-3 h-3" /> Historial
    </span>
  ) : (
    <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-semibold bg-emerald-50 text-emerald-700 px-2 py-0.5 rounded-full">
      <CalendarClock className="w-3 h-3" /> Activo
    </span>
  );

const TripCard = ({ trip, selected, onSelect, onEdit, onDelete }) => (
  <div
    role="button"
    tabIndex={0}
    onClick={() => onSelect(trip)}
    onKeyDown={(e) => e.key === "Enter" && onSelect(trip)}
    data-testid={`trip-card-${trip.id}`}
    className={`group text-left rounded-xl border p-4 cursor-pointer transition-[background-color,border-color,box-shadow,transform] duration-200 hover:-translate-y-0.5 ${
      selected ? "border-sky-500 bg-sky-50 shadow-md ring-2 ring-sky-200" : "border-sky-100 bg-white/80 hover:border-sky-300 hover:shadow"
    } ${trip.status === "history" ? "opacity-80" : ""}`}
  >
    <div className="flex items-start justify-between gap-2">
      <div className="min-w-0">
        <p className="font-semibold font-heading text-sky-900 truncate" data-testid={`trip-title-${trip.id}`}>
          <span className="mr-1">{TRIP_TYPE_EMOJI[trip.trip_type] || "🧳"}</span>
          {tripTitle(trip)}
        </p>
        <p className="text-xs text-sky-700/70 mt-0.5">{tripDatesLabel(trip)}</p>
      </div>
      <StatusBadge status={trip.status} />
    </div>
    <div className="flex items-center justify-between mt-3">
      <span className="text-xs text-sky-700 inline-flex items-center gap-1">
        <FileText className="w-3 h-3" /> {trip.vouchers_count || 0} voucher{trip.vouchers_count === 1 ? "" : "s"}
      </span>
      <div className="flex gap-1 opacity-70 group-hover:opacity-100 transition-opacity">
        <button type="button" title="Editar viaje" onClick={(e) => { e.stopPropagation(); onEdit(trip); }} className="p-1.5 rounded-md text-sky-600 hover:bg-sky-100" data-testid={`trip-edit-${trip.id}`}>
          <Pencil className="w-3.5 h-3.5" />
        </button>
        <button type="button" title="Eliminar viaje" onClick={(e) => { e.stopPropagation(); onDelete(trip); }} className="p-1.5 rounded-md text-red-500 hover:bg-red-50" data-testid={`trip-delete-${trip.id}`}>
          <Trash2 className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  </div>
);

export const TripsPanel = ({ trips, selectedTripId, onSelect, onNew, onEdit, onDelete }) => (
  <div className="mb-6" data-testid="trips-panel">
    <div className="flex items-center justify-between mb-3">
      <h2 className="text-base md:text-lg font-semibold font-heading text-sky-900">
        Viajes <span className="text-sky-600/70 font-normal">({trips.length})</span>
      </h2>
      <Button size="sm" onClick={onNew} className="btn-sky text-white" data-testid="new-trip-button">
        <Plus className="w-4 h-4 mr-1" /> Nuevo viaje
      </Button>
    </div>
    {trips.length === 0 ? (
      <p className="text-sm text-sky-700/60 border border-dashed border-sky-200 rounded-xl p-6 text-center" data-testid="trips-empty">
        Este cliente todavía no tiene viajes. Creá el primero con "Nuevo viaje".
      </p>
    ) : (
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {trips.map((t) => (
          <TripCard key={t.id} trip={t} selected={t.id === selectedTripId} onSelect={onSelect} onEdit={onEdit} onDelete={onDelete} />
        ))}
      </div>
    )}
  </div>
);
